import * as React from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

import { cn } from "@/frontend/lib/utils";
import { Button } from "@/frontend/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/frontend/components/ui/table";

export interface Column<T> {
  key: keyof T;
  label: string;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  format?: "currency" | "number" | "text";
  highlight?: boolean;
  mobileHidden?: boolean;
  className?: string;
  clickable?: boolean;
  formatFunction?: (value: any, row?: T) => string;
  renderFunction?: (value: any, row?: T) => React.ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  className?: string;
  onSort?: (key: keyof T, direction: "asc" | "desc") => void;
  sortKey?: keyof T;
  sortDirection?: "asc" | "desc";
  onCellClick?: (row: T, column: Column<T>) => void;
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  className,
  onSort,
  sortKey,
  sortDirection,
  onCellClick,
}: DataTableProps<T>) {
  const [internalSortKey, setInternalSortKey] = React.useState<keyof T | null>(
    null,
  );
  const [internalSortDirection, setInternalSortDirection] = React.useState<
    "asc" | "desc"
  >("asc");

  const currentSortKey = sortKey ?? internalSortKey;
  const currentSortDirection = sortDirection ?? internalSortDirection;

  const handleSort = (key: keyof T) => {
    const newDirection =
      currentSortKey === key && currentSortDirection === "asc" ? "desc" : "asc";

    if (onSort) {
      onSort(key, newDirection);
    } else {
      setInternalSortKey(key);
      setInternalSortDirection(newDirection);
    }
  };

  const sortedData = React.useMemo(() => {
    if (!currentSortKey) return data;

    return [...data].sort((a, b) => {
      const aValue = a[currentSortKey];
      const bValue = b[currentSortKey];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return currentSortDirection === "asc" ? 1 : -1;
      if (bValue == null) return currentSortDirection === "asc" ? -1 : 1;

      // Convert to comparable values
      let aCompare = aValue;
      let bCompare = bValue;

      if (typeof aValue === "string") {
        aCompare = aValue.toLowerCase();
        bCompare = bValue.toLowerCase();
      }

      if (aCompare < bCompare) {
        return currentSortDirection === "asc" ? -1 : 1;
      }
      if (aCompare > bCompare) {
        return currentSortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [data, currentSortKey, currentSortDirection]);

  const formatValue = (value: any, format?: string) => {
    if (value == null) return "-";

    switch (format) {
      case "currency":
        return new Intl.NumberFormat("th-TH", {
          style: "currency",
          currency: "THB",
          minimumFractionDigits: 2,
        }).format(Number(value));
      case "number":
        return new Intl.NumberFormat("th-TH").format(Number(value));
      default:
        return String(value);
    }
  };

  const getSortIcon = (column: Column<T>) => {
    if (!column.sortable) return null;

    if (currentSortKey !== column.key) {
      return <ChevronsUpDown className="ml-2 h-4 w-4" />;
    }

    return currentSortDirection === "asc" ? (
      <ChevronUp className="ml-2 h-4 w-4" />
    ) : (
      <ChevronDown className="ml-2 h-4 w-4" />
    );
  };

  const getColumnClassName = (column: Column<T>) => {
    const baseClasses = [];

    if (column.align === "center") baseClasses.push("text-center");
    if (column.align === "right") baseClasses.push("text-right");
    if (column.mobileHidden) baseClasses.push("hidden md:table-cell");
    if (column.className) baseClasses.push(column.className);

    return cn(baseClasses);
  };

  const getCellClassName = (column: Column<T>, value: any) => {
    const baseClasses = [];

    if (column.align === "center") baseClasses.push("text-center");
    if (column.align === "right") baseClasses.push("text-right");
    if (column.mobileHidden) baseClasses.push("hidden md:table-cell");
    if (column.highlight) baseClasses.push("font-semibold");

    // Add color classes based on value and format
    if (column.format === "currency" && typeof value === "number") {
      if (column.key.toString().includes("commission")) {
        baseClasses.push("text-green-700 font-bold");
      } else if (column.key.toString().includes("paid")) {
        baseClasses.push("text-green-600");
      } else if (column.key.toString().includes("pending")) {
        if (column.key.toString().includes("all")) {
          baseClasses.push("text-red-600");
        } else {
          baseClasses.push("text-orange-600");
        }
      }
    }

    return cn(baseClasses);
  };

  return (
    <div className={cn("viewport-fit-container", className)}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={String(column.key)}
                className={getColumnClassName(column)}
              >
                {column.sortable ? (
                  <Button
                    variant="ghost"
                    onClick={() => handleSort(column.key)}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    {column.label}
                    {getSortIcon(column)}
                  </Button>
                ) : (
                  column.label
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.length > 0 ? (
            sortedData.map((row, index) => (
              <TableRow key={index} className="hover:bg-gray-50">
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    className={cn(
                      getCellClassName(column, row[column.key]),
                      column.clickable && "cursor-pointer hover:bg-muted/50",
                    )}
                    onClick={() =>
                      column.clickable && onCellClick?.(row, column)
                    }
                  >
                    {column.renderFunction ? (
                      column.renderFunction(row[column.key], row)
                    ) : column.key === "invoiceCount" ? (
                      <span
                        className={cn(
                          "inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-sm font-semibold",
                          column.clickable &&
                            "hover:bg-blue-200 transition-colors",
                        )}
                      >
                        {column.formatFunction
                          ? column.formatFunction(row[column.key], row)
                          : formatValue(row[column.key], column.format)}
                      </span>
                    ) : (
                      column.formatFunction
                        ? column.formatFunction(row[column.key], row)
                        : formatValue(row[column.key], column.format)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
